import {
  Archive,
  ArrowDown,
  ArrowUp,
  Banknote,
  ClipboardCheck,
  Clock,
  Coins,
  Database,
  PackageCheck,
  Pencil,
  TriangleAlert,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AlertDTO, AlertType } from '@lightbm/shared';
import { useFormatters, type Formatters } from '@/app/app-context';
import type { BadgeTone } from '@/components/ui/Badge';
import { useAuth } from '@/features/auth/auth-context';
import { newOperationPath } from '@/features/operations/operations';
import { placeName } from '@/features/operations/parts';
import { cn } from '@/lib/cn';

/** Colours of the design "Alerts & notifications": the badge, the icon tile, the type name. */
export const ALERT_LOOKS: Record<
  AlertType,
  { icon: LucideIcon; tone: BadgeTone; tile: string; ink: string }
> = {
  negative_stock: {
    icon: TriangleAlert,
    tone: 'danger',
    tile: 'bg-danger-soft text-danger',
    ink: 'text-danger',
  },
  large_adjustment: {
    icon: Pencil,
    tone: 'info',
    tile: 'bg-info-soft text-info',
    ink: 'text-info',
  },
  low_stock: {
    icon: ArrowDown,
    tone: 'warning',
    tile: 'bg-warning-soft text-warning',
    ink: 'text-warning',
  },
  transfer_delayed: {
    icon: Truck,
    tone: 'warning',
    tile: 'bg-warning-soft text-warning',
    ink: 'text-warning',
  },
  out_of_stock: {
    icon: Archive,
    tone: 'neutral',
    tile: 'bg-neutral-soft text-ink-2',
    ink: 'text-ink-2',
  },
  count_variance: {
    icon: ClipboardCheck,
    tone: 'reversed',
    tile: 'bg-reversed-soft text-reversed',
    ink: 'text-reversed',
  },
  overstock: { icon: ArrowUp, tone: 'info', tile: 'bg-info-soft text-info', ink: 'text-info' },
  job_failed: {
    icon: Database,
    tone: 'danger',
    tile: 'bg-neutral-soft text-ink-2',
    ink: 'text-danger',
  },
  order_to_prepare: {
    icon: PackageCheck,
    tone: 'info',
    tile: 'bg-info-soft text-info',
    ink: 'text-info',
  },
  cod_overdue: {
    icon: Banknote,
    tone: 'warning',
    tile: 'bg-warning-soft text-warning',
    ink: 'text-warning',
  },
  shift_open_late: {
    icon: Clock,
    tone: 'warning',
    tile: 'bg-warning-soft text-warning',
    ink: 'text-warning',
  },
  cash_variance: {
    icon: Coins,
    tone: 'danger',
    tile: 'bg-danger-soft text-danger',
    ink: 'text-danger',
  },
};

export function AlertIcon({ type, className }: { type: AlertType; className?: string }) {
  const { icon: Icon, tile } = ALERT_LOOKS[type];
  return (
    <span
      className={cn(
        'flex size-12 shrink-0 items-center justify-center rounded-control',
        tile,
        className,
      )}
      aria-hidden="true"
    >
      <Icon className="size-[22px]" strokeWidth={1.9} />
    </span>
  );
}

const num = (value: unknown) => (typeof value === 'number' ? value : 0);

/** "5" for a whole limit, "6.2" otherwise. */
export const percentText = (format: Formatters, value: number) =>
  format.number(value, Number.isInteger(value) ? 0 : 1);

/** The alert in words, from the figures the engine wrote (same texts as the emails). */
export function useAlertText() {
  const { t } = useTranslation('alerts');
  const format = useFormatters();
  return (alert: AlertDTO): { title: string; detail: string } => {
    const d = alert.data;
    const decimals = alert.product?.decimals ?? 0;
    const quantity = (value: unknown) =>
      `${format.number(num(value), decimals)} ${alert.product?.unitSymbol ?? ''}`.trim();
    const product = alert.product?.name ?? '—';
    const number = alert.document?.number ?? '—';
    const place = placeName(alert.location) ?? t('inTotal');
    switch (alert.type) {
      case 'negative_stock':
        return {
          title: t('text.negative_stock.title', { product, quantity: quantity(d.quantity) }),
          detail: [
            d.confirmedBy
              ? t('text.negative_stock.confirmed', { person: String(d.confirmedBy) })
              : null,
            t('text.negative_stock.detail'),
          ]
            .filter(Boolean)
            .join(' '),
        };
      case 'large_adjustment': {
        const values = {
          value: d.value === null || d.value === undefined ? '—' : format.amount(num(d.value)),
          limit: format.amount(num(d.limit)),
          person: String(d.by ?? ''),
        };
        return {
          title: t('text.large_adjustment.title', { number }),
          detail: d.by
            ? t('text.large_adjustment.detailBy', values)
            : t('text.large_adjustment.detail', values),
        };
      }
      case 'low_stock':
        return {
          title: t('text.low_stock.title', { product }),
          detail: t(alert.location ? 'text.low_stock.detail' : 'text.low_stock.detailTotal', {
            quantity: quantity(d.quantity),
            min: quantity(d.min),
          }),
        };
      case 'transfer_delayed':
        return {
          title: t('text.transfer_delayed.title', { number, count: num(d.days) }),
          detail: t('text.transfer_delayed.detail', {
            date: typeof d.sentAt === 'string' ? format.date(d.sentAt) : '—',
            count: num(d.limit),
          }),
        };
      case 'out_of_stock':
        return {
          title: t('text.out_of_stock.title', { product }),
          detail:
            num(d.elsewhere) > 0
              ? t('text.out_of_stock.detail', { place, elsewhere: quantity(d.elsewhere) })
              : t('text.out_of_stock.detailNowhere', { place }),
        };
      case 'count_variance':
        return {
          title: t('text.count_variance.title', {
            number,
            percent: percentText(format, num(d.percent)),
          }),
          detail: t('text.count_variance.detail', {
            limit: percentText(format, num(d.limit)),
            count: num(d.lines),
            value: format.amount(num(d.value)),
          }),
        };
      case 'overstock':
        return {
          title: t('text.overstock.title', { product }),
          detail: t(alert.location ? 'text.overstock.detail' : 'text.overstock.detailTotal', {
            quantity: quantity(d.quantity),
            max: quantity(d.max),
          }),
        };
      case 'job_failed':
        return {
          title: t(d.job === 'backup' ? 'text.job_failed.backup' : 'text.job_failed.import'),
          detail: [d.name, d.error, t('text.job_failed.detail')].filter(Boolean).join(' · '),
        };
      case 'order_to_prepare':
        return {
          title: t('text.order_to_prepare.title', { number }),
          detail: t('text.order_to_prepare.detail', { count: num(d.lines) }),
        };
      case 'shift_open_late':
        return {
          title: t('text.shift_open_late.title', { register: String(d.register ?? '') }),
          detail: t('text.shift_open_late.detail', {
            cashier: String(d.cashier ?? ''),
            time: String(d.closeTime ?? ''),
          }),
        };
      case 'cash_variance':
        return {
          title: t('text.cash_variance.title', {
            register: String(d.register ?? ''),
            variance: format.amount(num(d.variance)),
          }),
          detail: t('text.cash_variance.detail', {
            cashier: String(d.cashier ?? ''),
            counted: format.amount(num(d.counted)),
            expected: format.amount(num(d.expected)),
          }),
        };
      case 'cod_overdue':
        return {
          title: t('text.cod_overdue.title', { number }),
          detail: t('text.cod_overdue.detail', {
            count: num(d.days),
            due: format.amount(num(d.due)),
          }),
        };
    }
  };
}

/** "Today 15:45 · Store Sousse", "Today 02:00 · Main Depot → Store Sfax". */
export function useAlertWhere() {
  const { t } = useTranslation('alerts');
  return (alert: AlertDTO) => {
    if (alert.route) {
      return `${placeName(alert.route.from) ?? '—'} → ${placeName(alert.route.to) ?? '—'}`;
    }
    if (alert.type === 'job_failed') return null;
    return placeName(alert.location) ?? t('inTotal');
  };
}

/** How a resolved alert closed. */
export function useResolutionText() {
  const { t } = useTranslation('alerts');
  const format = useFormatters();
  return (alert: AlertDTO) => {
    if (!alert.resolution || !alert.resolvedAt) return null;
    const date = format.moment(alert.resolvedAt);
    const { reason, reference, by } = alert.resolution;
    if (reason === 'cleared' && reference) return t('resolution.clearedBy', { reference });
    if (reason === 'read') return t('resolution.read', { person: by?.name ?? '—', date });
    if (reason === 'decided') {
      return t('resolution.decided', { reference: reference ?? alert.document?.number, date });
    }
    return t(`resolution.${reason}`, { date });
  };
}

/** The one thing to do about an alert: open its product or document, or start the fix. */
export function useAlertAction() {
  const { t } = useTranslation('alerts');
  const { can } = useAuth();
  return (alert: AlertDTO): { label: string; to: string } | null => {
    const product = alert.product ? `/products/${alert.product.id}` : null;
    const openProduct = product ? { label: t('actions.openProduct'), to: product } : null;
    switch (alert.type) {
      case 'negative_stock':
      case 'overstock':
        return openProduct;
      case 'low_stock':
        return can('stock:create') && alert.product
          ? {
              label: t('actions.receive'),
              to: newOperationPath('stockIn', {
                productId: alert.product.id,
                ...(alert.location ? { locationId: alert.location.id } : {}),
              }),
            }
          : openProduct;
      case 'out_of_stock':
        return can('transfers:create')
          ? { label: t('actions.transfer'), to: '/transfers/new' }
          : openProduct;
      case 'large_adjustment':
        return alert.document
          ? { label: t('actions.review'), to: `/operations/${alert.document.id}` }
          : null;
      case 'transfer_delayed':
        return alert.document
          ? { label: t('actions.openTransfer'), to: `/transfers/${alert.document.id}` }
          : null;
      case 'count_variance':
        return alert.document
          ? { label: t('actions.reviewCount'), to: `/counts/${alert.document.id}` }
          : null;
      case 'job_failed':
        return { label: t('actions.openDataTools'), to: '/data-tools' };
      case 'order_to_prepare':
        return alert.document
          ? { label: t('actions.openOrder'), to: `/orders/${alert.document.id}` }
          : null;
      case 'shift_open_late':
      case 'cash_variance':
        return alert.document
          ? { label: t('actions.openShift'), to: `/shifts/${alert.document.id}` }
          : null;
      case 'cod_overdue':
        return can('payments:record')
          ? { label: t('actions.settleCod'), to: '/payments/cod' }
          : alert.document
            ? { label: t('actions.openOrder'), to: `/orders/${alert.document.id}` }
            : null;
    }
  };
}
